// Pure transaction builders for the `briefField` node.
//
// Every structural change to a brief (move, add, delete, attr patch) goes
// through one of these, and every transaction they return carries
// BRIEF_STRUCTURAL_META so the guard plugin in BriefField.ts lets it past.
// They take an EditorState and hand back a Transaction, so the rules are
// testable without mounting an editor — see briefField.test.ts.

import type { Node as PMNode } from '@tiptap/pm/model';
import { TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import {
  BRIEF_FIELD_NODE,
  CUSTOM_FIELD_KEY,
  customBriefFieldAttrs,
  buildBriefFieldNode,
  getBriefFieldDefinition,
  isBuiltInFieldKey,
  normalizeBriefFieldAttrs,
  type BriefAudience,
  type BriefFieldAttrs,
} from '../../../shared/briefFields';
import { MAX_WARMUPS, warmupBody } from '../../../shared/warmupBank';

/**
 * Marks a transaction as a sanctioned structural change. The guard plugin
 * rejects any other transaction that adds, removes or duplicates a field, so
 * select-all + Delete and pasting a copied field are inert on a brief.
 */
export const BRIEF_STRUCTURAL_META = 'briefField:structural';

/** Where a field sits: its document position and top-level block index. */
export interface BriefFieldLocation {
  pos: number;
  index: number;
  node: PMNode;
}

/**
 * Every `briefField` id in the document, in order, duplicates included.
 *
 * The guard runs this twice per transaction on every script, brief or not, so
 * the walk stops at the first level that cannot hold a field: inside a field
 * (they never nest) and inside a textblock (its children are text and marks).
 * That keeps the cost proportional to the document's blocks rather than its
 * characters.
 */
export function briefFieldIds(doc: PMNode): string[] {
  const ids: string[] = [];
  doc.descendants((node) => {
    if (node.type.name === BRIEF_FIELD_NODE) {
      ids.push(String(node.attrs.id));
      return false;
    }
    return !node.isTextblock;
  });
  return ids;
}

/** The top-level `briefField` blocks, in document order. */
export function topLevelBriefFields(doc: PMNode): BriefFieldLocation[] {
  const out: BriefFieldLocation[] = [];
  let pos = 0;
  for (let index = 0; index < doc.childCount; index++) {
    const node = doc.child(index);
    if (node.type.name === BRIEF_FIELD_NODE) out.push({ pos, index, node });
    pos += node.nodeSize;
  }
  return out;
}

/** Find a field by id among the document's top-level blocks. */
export function findBriefField(doc: PMNode, id: string): BriefFieldLocation | null {
  let pos = 0;
  for (let index = 0; index < doc.childCount; index++) {
    const node = doc.child(index);
    if (node.type.name === BRIEF_FIELD_NODE && node.attrs.id === id) {
      return { pos, index, node };
    }
    pos += node.nodeSize;
  }
  return null;
}

function structural(tr: Transaction): Transaction {
  tr.setMeta(BRIEF_STRUCTURAL_META, true);
  return tr;
}

/** Replace the plain warm-up list, or add its field to an older brief. */
export function setBriefWarmupsTr(state: EditorState, texts: readonly string[]): Transaction | null {
  const cleaned = texts.map(text => text.trim()).filter(Boolean);
  if (cleaned.length > MAX_WARMUPS) return null;
  const existing = topLevelBriefFields(state.doc).find(field => field.node.attrs.key === 'warmup');
  const body = warmupBody(cleaned).map(node => state.schema.nodeFromJSON(node));
  if (existing) {
    const attrs = normalizeBriefFieldAttrs(existing.node.attrs);
    return structural(state.tr.replaceWith(existing.pos, existing.pos + existing.node.nodeSize,
      state.schema.nodes[BRIEF_FIELD_NODE].create(attrs, body)));
  }
  // This command is only offered by the brief editor, never an ordinary script.
  if (!topLevelBriefFields(state.doc).length) return null;
  const definition = getBriefFieldDefinition('warmup')!;
  const attrs = buildBriefFieldNode(definition).attrs;
  const questions = topLevelBriefFields(state.doc).find(field => field.node.attrs.key === 'questions');
  return structural(state.tr.insert(questions?.pos ?? state.doc.content.size,
    state.schema.nodes[BRIEF_FIELD_NODE].create(attrs, body)));
}

/**
 * Patch a field's attributes. Built-ins own their `title` and `key` — the
 * library, the PDF and question extraction all lean on them — so those keys
 * are dropped from the patch and attributes such as `audience`, `enabled` and `shoot` get through.
 */
export function patchBriefFieldAttrsTr(
  state: EditorState,
  id: string,
  patch: Partial<BriefFieldAttrs>,
): Transaction | null {
  const found = findBriefField(state.doc, id);
  if (!found) return null;

  const current = normalizeBriefFieldAttrs(found.node.attrs);
  const allowed: Partial<BriefFieldAttrs> = { ...patch };
  if (isBuiltInFieldKey(current.key)) {
    delete allowed.title;
    delete allowed.key;
  }
  delete allowed.id;

  const next = normalizeBriefFieldAttrs({ ...current, ...allowed });
  return structural(state.tr.setNodeMarkup(found.pos, undefined, next));
}

/**
 * Lift the field out and drop it back at `targetIndex`, counted among the
 * document's blocks *after* the removal. Everything else is a wrapper on this.
 */
function moveToIndexTr(
  state: EditorState,
  id: string,
  targetIndex: number,
): Transaction | null {
  const found = findBriefField(state.doc, id);
  if (!found) return null;

  const rest: PMNode[] = [];
  for (let i = 0; i < state.doc.childCount; i++) {
    if (i !== found.index) rest.push(state.doc.child(i));
  }
  const clamped = Math.max(0, Math.min(targetIndex, rest.length));
  // The document with the field removed is the one the insert position is
  // measured against, so the prefix of `rest` gives it directly.
  let insertPos = 0;
  for (let i = 0; i < clamped; i++) insertPos += rest[i].nodeSize;

  const tr = state.tr;
  tr.delete(found.pos, found.pos + found.node.nodeSize);
  tr.insert(insertPos, found.node);
  return structural(tr);
}

/**
 * Swap with the block above or below, but only when that block is another
 * field: a brief's title heading and client line stay put at the top.
 */
export function moveBriefFieldTr(
  state: EditorState,
  id: string,
  direction: 'up' | 'down',
): Transaction | null {
  const found = findBriefField(state.doc, id);
  if (!found) return null;

  const neighbourIndex = direction === 'up' ? found.index - 1 : found.index + 1;
  if (neighbourIndex < 0 || neighbourIndex >= state.doc.childCount) return null;
  if (state.doc.child(neighbourIndex).type.name !== BRIEF_FIELD_NODE) return null;

  // After the removal the neighbour sits at `found.index - 1` (up) or
  // `found.index` (down); land before it, or after it, respectively.
  return moveToIndexTr(state, id, direction === 'up' ? found.index - 1 : found.index + 1);
}

/** Drop `id` immediately before or after `targetId`. Used by the drawer's drag. */
export function moveBriefFieldToTr(
  state: EditorState,
  id: string,
  targetId: string,
  placement: 'before' | 'after',
): Transaction | null {
  if (id === targetId) return null;
  const found = findBriefField(state.doc, id);
  const target = findBriefField(state.doc, targetId);
  if (!found || !target) return null;

  // Re-index the target for the document with the moved field taken out.
  const targetIndex = target.index > found.index ? target.index - 1 : target.index;
  return moveToIndexTr(state, id, placement === 'before' ? targetIndex : targetIndex + 1);
}

/**
 * Append a new custom field holding one empty paragraph, and put the cursor
 * in it. Custom fields always arrive switched on, at the end, so you can
 * start typing straight away and drag it into place afterwards.
 */
export function addCustomBriefFieldTr(
  state: EditorState,
  input: { title: string; audience: BriefAudience },
  makeId?: () => string,
): Transaction | null {
  const fieldType = state.schema.nodes[BRIEF_FIELD_NODE];
  const paragraphType = state.schema.nodes.paragraph;
  if (!fieldType || !paragraphType) return null;

  const title = input.title.trim();
  if (!title) return null;

  const attrs = makeId
    ? customBriefFieldAttrs(title, input.audience, makeId())
    : customBriefFieldAttrs(title, input.audience);
  const node = fieldType.create(attrs, [paragraphType.create()]);

  // Land directly after the last field, not at the very end of the document:
  // the editor keeps a trailing paragraph there, and inserting past it would
  // strand that paragraph between fields and leave the new field with no
  // field neighbour for Move up / Move down.
  let pos = 0;
  let lastFieldEnd: number | null = null;
  for (let i = 0; i < state.doc.childCount; i++) {
    const child = state.doc.child(i);
    pos += child.nodeSize;
    if (child.type.name === BRIEF_FIELD_NODE) lastFieldEnd = pos;
  }
  pos = lastFieldEnd ?? state.doc.content.size;
  const tr = state.tr.insert(pos, node);
  // pos + 2 lands inside the field's first paragraph: +1 into the field,
  // +1 into the paragraph.
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 2)));
  tr.scrollIntoView();
  return structural(tr);
}

/**
 * Delete a custom field outright. Anything else refuses: a built-in's key is
 * a contract the library, the PDF and question extraction all read, and
 * hiding gives the same result reversibly. A key that is neither custom nor
 * in the library is a doc from another build, so it refuses too.
 */
export function deleteBriefFieldTr(state: EditorState, id: string): Transaction | null {
  const found = findBriefField(state.doc, id);
  if (!found) return null;
  const key = normalizeBriefFieldAttrs(found.node.attrs).key;
  if (key !== CUSTOM_FIELD_KEY) return null;

  return structural(state.tr.delete(found.pos, found.pos + found.node.nodeSize));
}

/**
 * Put a field node back at a given top-level index. Only used to undo a
 * delete from the toast, which is why it takes a node rather than an id.
 *
 * It refuses when the id is already in the document. Undo has two routes —
 * Cmd+Z and the toast's own button — and both can be taken for the same
 * delete; without this the second one would insert a node whose id already
 * exists, and a duplicate id is exactly what the guard cannot let stand.
 */
export function insertBriefFieldAtIndexTr(
  state: EditorState,
  node: PMNode,
  index: number,
): Transaction | null {
  if (node.type.name !== BRIEF_FIELD_NODE) return null;
  if (findBriefField(state.doc, String(node.attrs.id))) return null;
  const clamped = Math.max(0, Math.min(index, state.doc.childCount));
  let pos = 0;
  for (let i = 0; i < clamped; i++) pos += state.doc.child(i).nodeSize;
  return structural(state.tr.insert(pos, node));
}
